// Chakra Imports
import {
	Avatar,
	Button,
	Flex,
	Image,
	Link,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Text,
	useColorModeValue,
	useColorMode
} from '@chakra-ui/react';
import Icon from 'components/icons/Icon';
// Custom Components
import { ItemContent } from 'components/menu/ItemContent';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { SidebarResponsive } from 'components/sidebar/Sidebar';
// Auth Context
import { useAuth } from 'contexts/AuthContext';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate } from 'react-router-dom';
// Assets
import navImage from 'assets/img/layout/Navbar.png';
import { MdNotificationsNone, MdInfoOutline } from 'react-icons/md';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import { FaEthereum } from 'react-icons/fa';
import routes from 'routes';
export default function HeaderLinks(props: { secondary: boolean }) {
	const { secondary } = props;
	const { colorMode, toggleColorMode } = useColorMode();
	const { user, logout } = useAuth();
	const navigate = useNavigate();

	// Chakra Color Mode
	const navbarIcon = useColorModeValue('gray.700', 'white');
	let menuBg = useColorModeValue('white', 'navy.800');
	const menuListBg = useColorModeValue('white', 'navy.800');
	const textColor = useColorModeValue('navy.700', 'white');
	const textColorBrand = useColorModeValue('brand.700', 'brand.400');
	const ethColor = useColorModeValue('gray.700', 'white');
	const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
	const ethBg = useColorModeValue('secondaryGray.300', 'navy.900');
	const ethBox = useColorModeValue('white', 'navy.800');
	const shadow = useColorModeValue(
		'14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
		'14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
	);
	const borderButton = useColorModeValue('secondaryGray.500', 'whiteAlpha.200');

	// Handle logout
	const handleLogout = async () => {
		try {
			await logout();
			// Navigation will be handled automatically by the route protection in App.tsx
			// since isAuthenticated will become false after logout
		} catch (error) {
			console.error('Logout failed:', error);
			// Force redirect to login page even if logout fails
			navigate('/auth/sign-in');
		}
	};

	// Get user display name and role
	const userName = user?.email?.split('@')[0] || 'User';
	const userRole = user?.role || 'Guest';
	const displayName = `${userName.charAt(0).toUpperCase()}${userName.slice(1)}`;

	return (
		<Flex
			w={{ sm: '100%', md: 'auto' }}
			alignItems='center'
			flexDirection='row'
			bg={menuBg}
			flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
			p='10px'
			borderRadius='30px'
			boxShadow={shadow}>
			<SearchBar
				mb={() => {
					if (secondary) {
						return { base: '10px', md: 'unset' };
					}
					return 'unset';
				}}
				me='10px'
				borderRadius='30px'
			/>
			<Flex
				bg={ethBg}
				display={secondary ? 'flex' : 'none'}
				borderRadius='30px'
				ms='auto'
				p='6px'
				align='center'
				me='6px'>
				<Flex align='center' justify='center' bg={ethBox} h='29px' w='29px' borderRadius='30px' me='7px'>
					<Icon color={ethColor} w='9px' h='14px' as={FaEthereum} />
				</Flex>
				<Text w='max-content' color={ethColor} fontSize='sm' fontWeight='700' me='6px'>
					1,924
					<Text as='span' display={{ base: 'none', md: 'unset' }}>
						{' '}
						ETH
					</Text>
				</Text>
			</Flex>
			<Flex mr='10px'>
				<SidebarResponsive routes={routes} />
			</Flex>
			<Button
				variant='no-hover'
				bg='transparent'
				p='0px'
				minW='unset'
				minH='unset'
				h='18px'
				w='max-content'
				onClick={toggleColorMode}>
				<Icon
					me='10px'
					h='18px'
					w='18px'
					color={navbarIcon}
					as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
				/>
			</Button>
			<Menu>
				<MenuButton p='0px'>
					<Avatar
						_hover={{ cursor: 'pointer' }}
						color='white'
						name={displayName}
						bg='#11047A'
						size='sm'
						w='40px'
						h='40px'
					/>
				</MenuButton>
				<MenuList 
					boxShadow={shadow} 
					p='0px' 
					mt='10px' 
					borderRadius='20px' 
					bg={menuListBg} 
					border='none'
					_focus={{ bg: menuListBg }}
					_active={{ bg: menuListBg }}
				>
					<Flex w='100%' mb='0px'>
						<Text
							ps='20px'
							pt='16px'
							pb='10px'
							w='100%'
							borderBottom='1px solid'
							borderColor={borderColor}
							fontSize='sm'
							fontWeight='700'
							color={textColor}>
							👋&nbsp; Hey, {displayName}
						</Text>
					</Flex>
					<Flex flexDirection='column' p='10px'>
						<MenuItem 
							bg='transparent'
							_hover={{ bg: useColorModeValue('gray.100', 'navy.700') }} 
							_focus={{ bg: useColorModeValue('gray.100', 'navy.700') }} 
							borderRadius='8px' 
							px='14px'
						>
							<Flex flexDirection='column' align='start'>
								<Text fontSize='sm' fontWeight='600' color={textColor}>{user?.email || 'No email'}</Text>
								<Text fontSize='xs' color={useColorModeValue('gray.500', 'gray.400')}>{userRole}</Text>
							</Flex>
						</MenuItem>
						<MenuItem
							bg='transparent'
							_hover={{ bg: useColorModeValue('red.50', 'rgba(229, 62, 62, 0.2)') }}
							_focus={{ bg: useColorModeValue('red.50', 'rgba(229, 62, 62, 0.2)') }}
							color='red.400'
							borderRadius='8px'
							px='14px'
							onClick={handleLogout}>
							<Text fontSize='sm'>Log out</Text>
						</MenuItem>
					</Flex>
				</MenuList>
			</Menu>
		</Flex>
	);
}

HeaderLinks.propTypes = {
	variant: PropTypes.string,
	fixed: PropTypes.bool,
	secondary: PropTypes.bool,
	onOpen: PropTypes.func
};
